import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useRequest from "../../../../components/hook/use-req";
import { InstagramSender, ReachEstimate, buildContent, buildReachEstimateBody, parseInstagramContent, useDefaultAdLocation, useInstagramSenders } from "./api";
import { AdSet, BudgetState, CreativeState, InstagramAdState, createAdSet, createInitialState } from "./types";
import { AiPanel, LivePreviewPanel } from "./ui";
import StepDetails from "./step-details";
import StepMessaging from "./step-messaging";
import StepBudget from "./step-budget";
import StepReview from "./step-review";
import { apiErrorMessage } from "../../../../utils/function";

type Panel = "ai" | "preview";

// Steps 2–5 of the broadcast wizard when the channel is Instagram: Details
// (broadcast + ad set), Messaging, Budget and Schedule, Review and Launch.
// The wizard's header and step row stay the parent's; this only renders the
// body. Wired to the real Instagram ads API (api.ts): a campaign is created
// the first time the user leaves Details, every step transition autosaves
// the draft, Review validates before launch, and Launch calls the real
// PUT /campaign/update?launch=true.
const InstagramFlow: React.FC<{
  currentStep: number;
  setCurrentStep: (step: number) => void;
  channelOptions: { value: string; label: string }[];
  onChannelChange: (value: string) => void;
  onExit: () => void;
  // Resuming an existing draft from the broadcast dashboard — see the
  // hydration effect below. Both are undefined for a genuinely new broadcast.
  existingCampaignId?: string;
  existingContent?: any;
}> = ({ currentStep, setCurrentStep, channelOptions, onChannelChange, onExit, existingCampaignId, existingContent }) => {
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const [state, setState] = useState<InstagramAdState>(createInitialState);
  const [activeAdSet, setActiveAdSet] = useState(0);
  const [panel, setPanel] = useState<Panel>("preview");

  const { senders, loading: sendersLoading } = useInstagramSenders();
  const defaultLocation = useDefaultAdLocation();

  // Auto-picks the first sender once the list loads, if nothing's chosen yet
  // (mirrors the rest of the wizard's "pre-filled" defaults). A hydrated
  // draft's senderId (below) always wins once it arrives, even if this fired
  // first — the hydration effect replaces state wholesale.
  useEffect(() => {
    if (!state.senderId && senders.length > 0) {
      const s = senders[0];
      setState((prev) => ({ ...prev, senderId: s.id, senderHandle: s.instagram_account?.ig_username ? `@${s.instagram_account.ig_username}` : s.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senders]);

  // Resume an existing draft: hydrate local state from its already-saved
  // `content` exactly once, so continuing to edit doesn't start from
  // createInitialState() and silently drop everything picked in the earlier
  // session (see parseInstagramContent's own comment for why this matters —
  // it's what made save/launch always POST a brand-new campaign instead of
  // updating the existing one). `campaignIdRef` below is seeded synchronously
  // from `existingCampaignId` (available at mount, unlike `existingContent`
  // which arrives once its own fetch resolves).
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || !existingContent) return;
    hydratedRef.current = true;
    setState(parseInstagramContent(existingContent));
  }, [existingContent]);

  // Once senders load, fill in the display handle for a hydrated senderId
  // that arrived before senderHandle could be looked up (parseInstagramContent
  // only has the id, not the handle, from the saved content).
  useEffect(() => {
    if (!state.senderId || state.senderHandle || senders.length === 0) return;
    const match = senders.find((s) => s.id === state.senderId);
    if (match) update({ senderHandle: match.instagram_account?.ig_username ? `@${match.instagram_account.ig_username}` : match.name });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senders, state.senderId, state.senderHandle]);

  // The initial ad set is seeded with India before the user's real org
  // country resolves (createAdSet can't await an async fetch) — once it
  // does, replace it on any ad set that's still exactly that untouched seed,
  // the same "correct the default, don't fight a real edit" approach used
  // for the sender above.
  useEffect(() => {
    if (!defaultLocation) return;
    setState((prev) => {
      const isDefaultSeed = (a: AdSet) => a.locations.length === 1 && a.locations[0].key === "IN" && a.locations[0].type === "country";
      if (!prev.adSets.some(isDefaultSeed)) return prev;
      return { ...prev, adSets: prev.adSets.map((a) => (isDefaultSeed(a) ? { ...a, locations: [defaultLocation] } : a)) };
    });
  }, [defaultLocation]);

  useEffect(() => {
    setPanel("preview");
  }, [currentStep]);

  const update = (patch: Partial<InstagramAdState>) => setState((s) => ({ ...s, ...patch }));
  const updateCreative = (patch: Partial<CreativeState>) => setState((s) => ({ ...s, creative: { ...s.creative, ...patch } }));
  const updateBudget = (patch: Partial<BudgetState>) => setState((s) => ({ ...s, budget: { ...s.budget, ...patch } }));
  const updateAdSet = (patch: Partial<AdSet>) =>
    setState((s) => ({ ...s, adSets: s.adSets.map((a, i) => (i === activeAdSet ? { ...a, ...patch } : a)) }));
  const addAdSet = () => {
    setActiveAdSet(state.adSets.length);
    setState((s) => ({ ...s, adSets: [...s.adSets, createAdSet(undefined, defaultLocation || undefined)] }));
  };
  const onSenderChange = (sender: InstagramSender) =>
    update({ senderId: sender.id, senderHandle: sender.instagram_account?.ig_username ? `@${sender.instagram_account.ig_username}` : sender.name });

  // ── campaign lifecycle ────────────────────────────────────────────────
  // Seeded from an existing draft's id (available synchronously at mount,
  // unlike existingContent) so ensureCampaignId below never re-POSTs a
  // duplicate campaign when resuming — every save/launch goes straight to
  // the real PATCH/PUT against this same id instead.
  const campaignIdRef = useRef<string | null>(existingCampaignId || null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[] | null>(null);
  const [launching, setLaunching] = useState(false);

  const { makeRequest: createCampaign } = useRequest("/campaign", "POST", { Authorization: `Bearer ${token}` });
  // Save-as-draft and Launch both go through this same PUT /campaign/update
  // call now (per direct request) — only `launch` differs (false vs true).
  // The doc's separate PATCH /campaign/draft/save still exists server-side,
  // but PUT /campaign/update already accepts DRAFT/PAUSED campaigns with the
  // same content validation per its own description, so there's no need for
  // both call sites.
  const { makeRequest: updateCampaign } = useRequest("/campaign/update", "PUT", { Authorization: `Bearer ${token}` });
  const { makeRequest: validateCampaign } = useRequest("/instagram/ads", "GET", { Authorization: `Bearer ${token}` });

  // Creates the campaign draft the first time it's needed (leaving Details,
  // or an explicit Save as Draft) rather than the moment the flow opens —
  // so abandoning before finishing Details doesn't leave a stray draft.
  const ensureCampaignId = async (silent = false): Promise<string | null> => {
    if (campaignIdRef.current) return campaignIdRef.current;
    const [response, status] = await createCampaign({ campaign_name: state.name, campaign_channel: "INSTAGRAM", campaign_goal: state.goal });
    // POST /campaign's real response wraps the id under `.response` (same
    // shape campaign-setup.tsx already reads for every other channel:
    // `response?.response?.campaign_id`) — this previously checked
    // `.campaign_id`/`.data.campaign_id`, which never matched, so `id` was
    // always undefined and every autosave/launch below silently no-opped
    // (returned null before ever calling PUT /campaign/update) instead of
    // actually sending the full content.
    const id = response?.response?.campaign_id || response?.campaign_id || response?.data?.campaign_id;
    if (status >= 200 && status < 300 && id) {
      campaignIdRef.current = id;
      return id;
    }
    if (!silent) toast.error("Couldn't create the broadcast — try again.");
    return null;
  };

  const saveDraft = async (announce: boolean, silent = false) => {
    setSavingDraft(true);
    try {
      const id = await ensureCampaignId(silent);
      if (!id) return;
      const [response, status] = await updateCampaign({ campaign_id: id, campaign_channel: "INSTAGRAM", launch: false, content: buildContent(state, state.senderId) });
      if (status >= 200 && status < 300) {
        if (announce) toast.success("Draft saved");
      } else if (!silent) {
        toast.error(apiErrorMessage(response, "Couldn't save the draft."));
      }
    } catch {
      if (!silent) toast.error("Couldn't save the draft.");
    } finally {
      setSavingDraft(false);
    }
  };

  const next = async () => {
    // Belt-and-braces alongside each step's own `nextDisabled` (below) — the
    // button being disabled already blocks the click, but this keeps the
    // step transition itself from ever firing on an incomplete step
    // regardless of how `next` gets called.
    const stepValid = currentStep === 2 ? detailsValid : currentStep === 3 ? messagingValid : currentStep === 4 ? budgetValid : true;
    if (!stepValid) return;
    // Best-effort autosave — a failed save shouldn't trap the user on the
    // step they're trying to leave (Save as Draft and Launch still surface
    // their own errors).
    await saveDraft(false, true);
    setCurrentStep(Math.min(currentStep + 1, 5));
  };
  const back = () => setCurrentStep(Math.max(currentStep - 1, 2));

  // Runs GET /instagram/ads/{id}/validate as soon as Review is reached.
  useEffect(() => {
    if (currentStep !== 5) return;
    (async () => {
      const id = campaignIdRef.current;
      if (!id) {
        setValidationErrors(null);
        return;
      }
      setValidating(true);
      try {
        const [response, status] = await validateCampaign(undefined, undefined, `${id}/validate`);
        if (status >= 200 && status < 300) {
          setValidationErrors(Array.isArray(response?.errors) ? response.errors : []);
        } else {
          setValidationErrors(null);
        }
      } finally {
        setValidating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const handleLaunch = async () => {
    setLaunching(true);
    try {
      const id = await ensureCampaignId();
      if (!id) return;
      const [response, status] = await updateCampaign({ campaign_id: id, campaign_channel: "INSTAGRAM", launch: true, content: buildContent(state, state.senderId) });
      if (status >= 200 && status < 300) {
        toast.success("Broadcast launched");
        navigate("/campaign");
      } else {
        toast.error(apiErrorMessage(response, "Couldn't launch this broadcast — check the errors above and try again."));
      }
    } catch {
      toast.error("Couldn't launch this broadcast — try again.");
    } finally {
      setLaunching(false);
    }
  };

  // ── reach estimate (Details step, active ad set) ───────────────────────
  const { makeRequest: reachEstimateRequest } = useRequest("/instagram/ads/reach-estimate", "POST", { Authorization: `Bearer ${token}` });
  const [reach, setReach] = useState<ReachEstimate | null>(null);
  const [reachLoading, setReachLoading] = useState(false);
  const activeAdSetData = state.adSets[activeAdSet];

  useEffect(() => {
    if (currentStep !== 2 || !state.senderId || !activeAdSetData || activeAdSetData.locations.length === 0) {
      setReach(null);
      return;
    }
    let cancelled = false;
    setReachLoading(true);
    const t = setTimeout(async () => {
      const [response, status] = await reachEstimateRequest(buildReachEstimateBody(activeAdSetData, state.goal, state.senderId));
      if (cancelled) return;
      setReachLoading(false);
      setReach(status >= 200 && status < 300 ? response?.data || response : null);
    }, 700);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, state.senderId, state.goal, JSON.stringify(activeAdSetData)]);

  // Gates each step's Continue button the same way the standard broadcast
  // flow does (campaign-setup.tsx disables its Continue until an audience is
  // picked) — missing a required field here shouldn't let the user proceed
  // to a step that Review/validate will only reject later. Now enforced (the
  // senders/media-upload/etc. APIs it depends on are live) — see each step's
  // `nextDisabled` prop below.
  const needsPromotedObject = state.goal === "Sales" || state.goal === "Leads";
  const detailsValid =
    !!state.senderId &&
    state.name.trim().length > 0 &&
    state.adSets.every((a) => a.locations.length > 0 && (!needsPromotedObject || a.promotedObjectPixelId.trim().length > 0));
  const messagingValid = !!state.creative.media && state.creative.primaryText.trim().length > 0 && !!state.creative.cta;
  const budgetValid = state.budget.amount > 0 && !!state.budget.startAt && (!state.budget.hasEnd || !!state.budget.endAt);

  const phone = {
    handle: state.senderHandle || "@yourbrand",
    media: state.creative.media,
    primaryText: state.creative.primaryText,
    headline: state.creative.headline,
    cta: state.creative.cta,
  };

  const sidePanel =
    panel === "ai" ? (
      <AiPanel onClose={() => setPanel("preview")} />
    ) : (
      <LivePreviewPanel
        showAudience={currentStep === 2 || currentStep === 4}
        showReach={currentStep !== 3}
        showPhone={currentStep === 3 || currentStep === 5}
        phone={phone}
        reachLoading={reachLoading}
        reachEstimate={reach}
        reachNeedsLocation={currentStep === 2 && (!activeAdSetData || activeAdSetData.locations.length === 0)}
        onOpenAi={() => setPanel("ai")}
      />
    );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
      <div className="min-w-0">
        {currentStep === 2 && (
          <StepDetails
            state={state}
            update={update}
            channelOptions={channelOptions}
            onChannelChange={onChannelChange}
            senders={senders}
            sendersLoading={sendersLoading}
            onSenderChange={onSenderChange}
            adSets={state.adSets}
            activeAdSet={activeAdSet}
            setActiveAdSet={setActiveAdSet}
            updateAdSet={updateAdSet}
            addAdSet={addAdSet}
            onBack={onExit}
            onNext={next}
            onSaveDraft={() => saveDraft(true)}
            savingDraft={savingDraft}
            nextDisabled={!detailsValid}
          />
        )}
        {currentStep === 3 && (
          <StepMessaging
            broadcastName={state.name}
            creative={state.creative}
            updateCreative={updateCreative}
            senderId={state.senderId}
            onBack={back}
            onNext={next}
            onSaveDraft={() => saveDraft(true)}
            savingDraft={savingDraft}
            nextDisabled={!messagingValid}
          />
        )}
        {currentStep === 4 && (
          <StepBudget
            budget={state.budget}
            update={updateBudget}
            onBack={back}
            onNext={next}
            onSaveDraft={() => saveDraft(true)}
            savingDraft={savingDraft}
            nextDisabled={!budgetValid}
          />
        )}
        {currentStep === 5 && (
          <StepReview
            state={state}
            goToStep={setCurrentStep}
            onBack={back}
            onLaunch={handleLaunch}
            validating={validating}
            validationErrors={validationErrors}
            launching={launching}
          />
        )}
      </div>
      <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800 min-h-[420px] flex flex-col self-start lg:sticky lg:top-24">
        {sidePanel}
      </div>
    </div>
  );
};

export default InstagramFlow;
